/// <reference types="@types/google.maps" />

import { Loader } from '@googlemaps/js-api-loader';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { Box } from '@fhs-legacy/universal-components';
import { useApiKey } from 'hooks/configs/use-api-key';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { useLocale } from 'state/intl';
import { getRandomId } from 'utils';
import { isValidPosition } from 'utils/geolocation';
import DEFAULT_MAP_ZOOM from 'utils/restaurant/default-map-zoom';

import mapDefaults from './defaults';
import { svgToUri } from './icons';
import { destinationMarkerSvg } from './icons/destination-marker';
import { driverMarkerSvg } from './icons/driver-marker';
import { storeMarkerSvg } from './icons/store-marker';
import { storeActiveMarkerSvg } from './icons/store-marker-active';
import { storeDisabledMarkerSvg } from './icons/store-marker-disabled';
import { storeFavoriteMarkerSvg } from './icons/store-marker-fav';
import { storeFavoriteClosedMarkerSvg } from './icons/store-marker-fav-closed';
import { userMarkerSvg } from './icons/user-marker';
import {
  CenterPoint,
  FitAndCenterFromCoordsFn,
  IMarkerInput,
  IUseMapHook,
  MarkerTypes,
  PanToFn,
} from './types';

const MapContainer = Box.withConfig({
  backgroundColor: Styles.color.white,
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  height: 'full',
  width: 'full',
});

const markersUris = {
  [MarkerTypes.User]: svgToUri(userMarkerSvg),
  [MarkerTypes.StoreActive]: svgToUri(storeMarkerSvg),
  [MarkerTypes.StoreInActive]: svgToUri(storeActiveMarkerSvg),
  [MarkerTypes.StoreDisabled]: svgToUri(storeDisabledMarkerSvg),
  [MarkerTypes.StoreFavOpen]: svgToUri(storeFavoriteMarkerSvg),
  [MarkerTypes.StoreFavClosed]: svgToUri(storeFavoriteClosedMarkerSvg),
  [MarkerTypes.Destination]: svgToUri(destinationMarkerSvg),
  [MarkerTypes.Driver]: svgToUri(driverMarkerSvg),
};

const useMap: IUseMapHook = ({ eventListeners = {}, position, disableControls = false } = {}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markers = useRef<Record<string, { marker: google.maps.Marker; type: MarkerTypes }>>({});
  const googleMapsApiKey = useApiKey({ key: 'googleMaps', isRegionalized: false });

  // track when the map container has been loaded into the dom
  const [isMapMounted, setIsMapMounted] = useState(false);
  const domRef = useRef<HTMLDivElement | null>(null);
  const { libraryLoaded } = useGoogleGeolocationLibrary();

  const { region } = useLocale();
  const defaultPosition = mapDefaults[region] ?? mapDefaults.default;
  const startPosition = position
    ? {
        lat: position.lat,
        lng: position.lng,
      }
    : defaultPosition;
  const [center, setCenter] = useState<CenterPoint>(startPosition);
  const [zoom, setZoom] = useState(
    startPosition !== defaultPosition ? defaultPosition.zoom : DEFAULT_MAP_ZOOM
  );

  const mapListeners = useRef<Record<string, google.maps.MapsEventListener[]>>({});

  const addListener = (eventName: string, map: google.maps.Map, cb: Function, id: string) => {
    if (!mapListeners.current[id]) {
      mapListeners.current[id] = [];
    }

    const dereg = map.addListener(eventName, cb);
    mapListeners.current[id] = mapListeners.current[id].concat(dereg);
  };

  const clearListenerCache = (id: string) => {
    const handlers = mapListeners.current[id] || [];
    handlers.forEach(handler => {
      if (typeof handler.remove === 'function') {
        handler.remove();
      }
    });
    mapListeners.current[id] = [];
  };

  /*
   * SETUP MAP
   *
   * This loads the map api internally, sets up some options
   * and begins the whole process.
   *
   * NOTES:
   * - This needs to be an effect
   * - It has required listener clean ups
   * - Can not re-render map created from this hook, will error.
   *
   */

  const prepareMap = useCallback(
    (mapId: string): void => {
      const options = {
        center: startPosition,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
        disableDefaultUI: true,
        zoom,
        gestureHandling: disableControls ? 'none' : 'greedy',
      } as google.maps.MapOptions;

      const loader = new Loader({
        apiKey: googleMapsApiKey,
        // Keep Google's map behavior (including geocoding and labels) aligned
        // with the regional app build.
        region,
      });

      loader.importLibrary('maps').then(({ Map }) => {
        const mapInstance = new Map(domRef?.current!, options);
        const setClampedMapBounds = () => {
          const mapCenter = mapInstance.getCenter() as google.maps.LatLng;

          ReactDOM.unstable_batchedUpdates(() => {
            setCenter({
              lat: mapCenter.lat(),
              lng: mapCenter.lng(),
            });
          });
        };

        window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
          const mapZoom = mapInstance.getZoom();
          setZoom(mapZoom!);
          setClampedMapBounds();
        });

        /**
         *  Called by dragstart and zoom_changed listeners to prevent keyboard remaining open on android devices
         *  when dragging/zooming map while search field has focus
         */
        const blurActiveElement = () => {
          const activeElement = document.activeElement as HTMLElement | null;
          activeElement?.blur?.();
        };

        addListener('dragstart', mapInstance, blurActiveElement, mapId);

        addListener('bounds_changed', mapInstance, debounce(setClampedMapBounds, 500), mapId);

        addListener(
          'zoom_changed',
          mapInstance,
          debounce(() => {
            const mapZoom = mapInstance.getZoom();
            setZoom(mapZoom!);
          }, 500),
          mapId
        );

        Object.keys(eventListeners).forEach(eventName => {
          const handlers = eventListeners[eventName];
          if (typeof handlers === 'function') {
            addListener(eventName, mapInstance, handlers, mapId);
          }
          if (Array.isArray(handlers)) {
            handlers.forEach(handler => {
              addListener(eventName, mapInstance, handler, mapId);
            });
          }
        });
        // Set a minZoom option to prevent the user to zoom out too much
        mapInstance.setOptions({ minZoom: 5 });
        setMap(mapInstance);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableControls, eventListeners, region, zoom]
  );

  useEffect(() => {
    const shouldLoad = !!domRef.current;

    if (!shouldLoad) {
      return;
    }

    const mapId = getRandomId();

    prepareMap(mapId);

    return () => clearListenerCache(mapId);
    /*
     * NOTE:
     * - Only run when we know the map container has been loaded in the dom ~ isMapMounted
     * - isMapMounted starts as false and is only set to true when we have a dom ref
     * - And the domRef.current will be set
     */
  }, [isMapMounted]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoadedIntoDom = useCallback((ref: HTMLDivElement) => {
    if (!domRef.current && ref) {
      domRef.current = ref;
      setIsMapMounted(true);
    }
  }, []);

  const resetZoom = useCallback(async () => {
    if (!map) {
      return;
    }

    map.setZoom(DEFAULT_MAP_ZOOM);
  }, [map]);

  const panTo = useCallback<PanToFn>(
    async newPosition => {
      if (!map) {
        return;
      }

      if (isValidPosition(newPosition)) {
        map.panTo(newPosition);
      }

      if (map.getZoom()! < DEFAULT_MAP_ZOOM && newPosition !== defaultPosition) {
        map.setZoom(DEFAULT_MAP_ZOOM);
      }
    },
    [defaultPosition, map]
  );

  useEffect(() => {
    if (map && isValidPosition(position)) {
      panTo(position);
    }
    // We recreate a new object above each render, which makes a simple object
    // compare fail. We only want to run this effect when the actual values change
  }, [map, position?.lat, position?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Creates a marker for a given type and location. If marker
   * already placed on map, updates accordingly.
   */
  const createMarker = useCallback(
    (markerInput: IMarkerInput): void => {
      if (!libraryLoaded || !map || !isMapMounted) {
        return;
      }

      const { type, location, onPress } = markerInput;

      if (!isValidPosition(location)) {
        return; // bail if location is not known
      }

      // This updates the marker if it has already been added to map.
      const isSingleUseMarker = [MarkerTypes.User, MarkerTypes.Driver].includes(type);
      const key = isSingleUseMarker ? type : `${location.lat}${location.lng}`;

      const existingMarker = markers.current[key]?.marker;
      if (existingMarker) {
        existingMarker.setMap(map);

        window.google.maps.event.clearInstanceListeners(existingMarker);
        window.google.maps.event.addListener(existingMarker, 'click', onPress!);

        // Update Icon
        if (markers.current[key].type !== type) {
          markers.current[key].type = type;
          existingMarker.setIcon(markersUris[type]);
        }

        // Update position
        existingMarker.setPosition(location);
        return;
      }

      const icon = markersUris[type];
      const marker = new window.google.maps.Marker({
        position: location,
        map,
        clickable: !!onPress,
        icon,
      });
      if (onPress) {
        window.google.maps.event.addListener(marker, 'click', onPress);
      }

      markers.current[key] = { marker, type };
      return;
    },
    [libraryLoaded, map, isMapMounted]
  );

  const fitAndCenterFromCoords = useCallback<FitAndCenterFromCoordsFn>(
    (coordsArray: CenterPoint[]) => {
      if (!libraryLoaded || !map || !isMapMounted) {
        return;
      }

      if (coordsArray.length >= 2) {
        const markerBounds = coordsArray.reduce((bounds, coord) => {
          bounds.extend(coord);
          return bounds;
        }, new window.google.maps.LatLngBounds());

        const marker1 = new window.google.maps.LatLng({
          lat: markerBounds.getSouthWest().lat(),
          lng: markerBounds.getSouthWest().lng(),
        });
        const marker2 = new window.google.maps.LatLng({
          lat: markerBounds.getNorthEast().lat(),
          lng: markerBounds.getNorthEast().lng(),
        });

        if (!marker1.equals(marker2)) {
          // Adding a 100px padding around the bounds to cover for
          // icons at the edge
          map.fitBounds(markerBounds, 100);
        }
      }
    },
    [libraryLoaded, map, isMapMounted]
  );

  const mapElement = React.createElement(MapContainer, { ref: onLoadedIntoDom });

  return {
    center,
    createMarker,
    fitAndCenterFromCoords,
    map: mapElement,
    panTo,
    zoom,
    resetZoom,
    isMapReady: isMapMounted,
  };
};

export default useMap;
